import { useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useDropdowns } from "@/hooks/useMeta";
import { DealerSelect } from "@/components/DealerSelect";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const ZONES = ["North", "South", "West", "Exports"];

export function DealInfoDialog({ open, onOpenChange, deal, onSaved }) {
  const { data: dd } = useDropdowns();
  const [f, setF] = useState({});
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));

  const openChange = (v) => {
    if (v && deal) {
      setF({
        zone: deal.zone || "", branch: deal.branch || "", univic_specified: deal.univic_specified || "",
        dealer_involved: deal.dealer_involved || "", dealer_record_id: deal.dealer_record_id || null,
        identified: deal.identified || "", architect: deal.architect || "", dnb_gc: deal.dnb_gc || "",
        end_client_name: deal.end_client_name || "", end_client_phone: deal.end_client_phone || "",
        end_client_email: deal.end_client_email || "", competition: deal.competition || "",
        product_type: deal.product_type || "", area_lacs_sqft: deal.area_lacs_sqft || "",
      });
    }
    onOpenChange(v);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/opportunities/${deal.id}`, f);
      toast.success("Required info saved");
      onOpenChange(false); onSaved?.();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const T = ([k, label, ph]) => (
    <div className="space-y-1.5" key={k}>
      <Label className="text-xs font-medium text-slate-700">{label}</Label>
      <Input value={f[k] || ""} onChange={(e) => set(k, e.target.value)} placeholder={ph || ""} className="h-9" data-testid={`opp-${k}`} />
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={openChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="deal-info-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Complete Required Info</DialogTitle>
          <DialogDescription>These fields are required before the opportunity can move beyond Quotation.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label className="text-xs font-medium text-slate-700">Zone</Label>
            <Select value={f.zone || ""} onValueChange={(v) => set("zone", v)}>
              <SelectTrigger className="h-9" data-testid="opp-zone"><SelectValue placeholder="Select zone" /></SelectTrigger>
              <SelectContent>{ZONES.map((z) => <SelectItem key={z} value={z}>{z}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {T(["branch", "Branch", "e.g. Mumbai"])}
          <div className="space-y-1.5">
            <Label className="text-xs font-medium text-slate-700">UniVicoustic Specified</Label>
            <Select value={f.univic_specified || ""} onValueChange={(v) => set("univic_specified", v)}>
              <SelectTrigger className="h-9" data-testid="opp-univic_specified"><SelectValue placeholder="Yes / No" /></SelectTrigger>
              <SelectContent><SelectItem value="Yes">Yes</SelectItem><SelectItem value="No">No</SelectItem></SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-medium text-slate-700">Dealer Involved</Label>
            <DealerSelect zone={f.zone} dealerInvolved={f.dealer_involved} dealerRecordId={f.dealer_record_id}
              onChange={(v) => setF((x) => ({ ...x, ...v }))} testid="opp-dealer-involved" />
          </div>
          {T(["identified", "Identified (date / stage)", "e.g. Design stage, Apr 2026"])}
          {T(["architect", "Architect / Designer / Lead Designer"])}
          {T(["dnb_gc", "D&B Firm / General Contractor"])}
          {T(["end_client_name", "End Client — Decision Maker"])}
          {T(["end_client_phone", "End Client — Contact Number"])}
          {T(["end_client_email", "End Client — Email"])}
          {T(["competition", "Competition"])}
          <div className="space-y-1.5">
            <Label className="text-xs font-medium text-slate-700">Type of Product</Label>
            <Select value={f.product_type || ""} onValueChange={(v) => set("product_type", v)}>
              <SelectTrigger className="h-9" data-testid="opp-product_type"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>{(dd?.product_type || ["HD", "VMT", "Both HD/VMT", "Ceiling Products"]).map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {T(["area_lacs_sqft", "Total Area (Lakh sqft)", "e.g. 1.5"])}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="opp-info-cancel">Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-slate-900 hover:bg-slate-800 gap-2" data-testid="opp-info-save">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}Save Info
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
